/* eslint-disable camelcase */
import PropTypes from 'prop-types'
import Link from 'next/link'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Call from '@material-ui/icons/Call'
import { IconMarker } from '@components/atoms/icons'
import { JsonLd } from '@components/atoms'
import defaults from '@config/defaults'
import Breadcrumbs from '@components/breadcrumbs'
import SearchCourse from '@components/search-course'
import { convertToSlug, transformCasing } from '@utils/slugs'
import secureLink from '@utils/secureLink'
import { isRenderableFacilitySchemaOrg } from '@lib/golf-facilities/adapters/facility-detail'
import { NextSeo } from 'next-seo'
import NoSsr from '@material-ui/core/NoSsr'
import GroupEventsNearFacility from '@components/group-events-near-facility/index'

import Coaches from './coaches'
import { FieldBox, Image, useStyles } from './facility-page.styles'

function FacilityPage(props) {
  const {
    facility_name,
    address1,
    address2,
    zip,
    phone,
    photo_facility,
    prices,
    state,
    state_name,
    city,
    operations_type_label,
    course,
    id,
    schemaOrg,
  } = props

  const s = useStyles()
  const defaultImg = defaults.facilityImage
  const PRIVATE_EQUITY = 'Private Equity'

  const photoFacility = secureLink(photo_facility)
  const courseURL = `https://www.pga.com/play/${state}/${convertToSlug(city)}/${course}/${id}`.toLowerCase()
  const phoneToCall = `tel:${phone}`
  const mapToOpen = `${defaults.googleMapsSearchUrl}${address1},${address2}`
  const metaTitle = `${facility_name} | ${city}, ${state} | PGA of America`
  const metaDescription = `Play golf at ${facility_name}${address1 ? `, located at ${address1} ${city}, ${state} ${zip}` : ''}${phone ? `. Call ${phone} for more information.` : '.'}`
  const facilityJsonLd = isRenderableFacilitySchemaOrg(schemaOrg)
    ? schemaOrg
    : {
      '@type': 'GolfCourse',
      name: facility_name,
      url: courseURL,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address1,
        addressLocality: city,
        addressRegion: state,
        postalCode: zip,
        addressCountry: 'United States',
      },
    }

  return (
    <div>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={metaTitle}
        description={metaDescription}
        canonical={courseURL}
        openGraph={{
          type: 'website',
          title: metaTitle,
          url: courseURL,
        }}
      />
      <JsonLd {...{
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': 'https://www.pga.com/play',
            name: 'Golf Courses',
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': `https://www.pga.com/play/${state}`.toLowerCase(),
            name: `${state}`,
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@id': `https://www.pga.com/play/${state}/${convertToSlug(city)}`.toLowerCase(),
            name: `${transformCasing(city)}`,
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@id': `${courseURL}`,
            name: `${transformCasing(facility_name)}`,
          },
        }],
      }}
      />
      <JsonLd {...facilityJsonLd} />
      <Box py={[4, 6, 8]}>
        <Container>

          <Box mb={[4, 4, 6]}>
            <Breadcrumbs component={Breadcrumbs}>
              <Link href="/play">Play</Link>
              <Link href={`/play/${state}`.toLowerCase()}>{state_name}</Link>
              <Link href={`/play/${state}/${convertToSlug(city)}`.toLowerCase()}>{city}</Link>
              <Box color="text.primary" component="span">{facility_name}</Box>
            </Breadcrumbs>
          </Box>

          <Box mb={[4, 4, 6]}>
            <SearchCourse dark />
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Image src={`${photoFacility || defaultImg}?h=600&w=900&fit=fill&fl=progressive&q=80`} alt={facility_name} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h4" data-testid="facility-name">{facility_name}</Typography>

              <Box py={4} mt={[3, 3, 4]} borderTop={1} borderColor="divider" data-testid="course-contacts">
                <FieldBox>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={mapToOpen}
                    className={s.facilityAddress}
                  >
                    <IconMarker />
                    {address1}, {address2}
                  </a>
                </FieldBox>
                {(operations_type_label === PRIVATE_EQUITY)
                  ? (
                    <Typography paragraph>
                      Play is limited to members at this private facility.
                    </Typography>
                  )
                  : (phone
                && (
                  <FieldBox icon="call">
                    <a
                      href={phoneToCall}
                      className={s.facilityAddress}
                    >
                      <Call color="primary" fontSize="small" />
                      {phone}
                    </a>
                  </FieldBox>
                )
                  )}
              </Box>

              {prices && <Typography paragraph>Price: {prices}</Typography>}
            </Grid>
          </Grid>
        </Container>
        <NoSsr>
          <GroupEventsNearFacility
            address={address1}
            city={city}
            state={state}
            zip={zip}
            facilityName={facility_name}
          />
        </NoSsr>
        <Container>
          <Coaches id={id} />
        </Container>
      </Box>
    </div>
  )
}

FacilityPage.propTypes = {
  facility_name: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  zip: PropTypes.string,
  phone: PropTypes.string,
  prices: PropTypes.string,
  state: PropTypes.string,
  state_name: PropTypes.string,
  city: PropTypes.string,
  photo_facility: PropTypes.string,
  operations_type_label: PropTypes.string,
  course: PropTypes.string,
  id: PropTypes.string,
  /** JSON-LD from golf-facilities SDK `profile.schemaOrg` when datasource is ON */
  schemaOrg: PropTypes.object,
}

export default FacilityPage
